import React, { useState } from 'react';
import { X, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import './PreInscricaoModal.css';
import API_BASE_URL from '../../../config/api';

const API_BASE = import.meta.env.VITE_API_URL || API_BASE_URL;

const PreInscricaoModal = ({ modalidade, onClose, modalidades = [] }) => {
  const [selectedSport, setSelectedSport] = useState(modalidade || modalidades[0] || null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    nif: '',
    dataNascimento: '',
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_BASE}/api/pre-inscricoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: form.nome,
          email: form.email,
          telefone: form.telefone,
          nif: form.nif || null,
          dataNascimento: form.dataNascimento,
          modalidadeNome: selectedSport?.name || '',
          modalidadeId: selectedSport?.id || 0,
        }),
      });

      if (!res.ok) throw new Error('Erro ao enviar pedido.');
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg('Não foi possível enviar a pré-inscrição. Tente novamente.');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="preinscricao-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className="preinscricao-modal">
        <button className="preinscricao-close" onClick={onClose} aria-label="Fechar">
          <X size={20} />
        </button>

        {status === 'success' ? (
          <div className="preinscricao-success">
            <div className="preinscricao-success-icon">
              <CheckCircle size={52} />
            </div>
            <h3>Pedido Enviado!</h3>
            <p>
              Recebemos o seu interesse na modalidade de <strong>{selectedSport?.name || ''}</strong>.<br />
              A nossa equipa irá entrar em contacto consigo em breve.
            </p>
            <button className="preinscricao-btn" onClick={onClose}>
              Fechar
            </button>
          </div>
        ) : (
          <>
            <div className="preinscricao-header">
              <div className="preinscricao-badge">Pré-Inscrição</div>
              {modalidades && modalidades.length > 0 ? (
                <div className="preinscricao-title-select-wrapper">
                  <button
                    type="button"
                    className="preinscricao-title-trigger"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {selectedSport ? selectedSport.name : 'Pré-Inscrição'}
                    <span className={`trigger-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>
                  {isDropdownOpen && (
                    <>
                      <div className="preinscricao-dropdown-backdrop" onClick={() => setIsDropdownOpen(false)} />
                      <ul className="preinscricao-title-dropdown">
                        {modalidades.map((m) => (
                          <li key={m.id}>
                            <button
                              type="button"
                              className={`dropdown-option ${selectedSport?.id === m.id ? 'active' : ''}`}
                              onClick={() => {
                                setSelectedSport(m);
                                setIsDropdownOpen(false);
                              }}
                            >
                              {m.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              ) : (
                <h2 className="preinscricao-title">{selectedSport?.name || 'Pré-Inscrição'}</h2>
              )}
              <p className="preinscricao-subtitle">
                Preencha os seus dados e entraremos em contacto para finalizar a inscrição.
              </p>
            </div>

            <form className="preinscricao-form" onSubmit={handleSubmit} noValidate>
              <div className="preinscricao-field">
                <label htmlFor="preinscricao-nome">Nome Completo *</label>
                <input
                  id="preinscricao-nome"
                  name="nome"
                  type="text"
                  placeholder="Ex: João Silva"
                  value={form.nome}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                />
              </div>

              <div className="preinscricao-row">
                <div className="preinscricao-field">
                  <label htmlFor="preinscricao-email">Email *</label>
                  <input
                    id="preinscricao-email"
                    name="email"
                    type="email"
                    placeholder="email@exemplo.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="preinscricao-field">
                  <label htmlFor="preinscricao-telefone">Telefone *</label>
                  <input
                    id="preinscricao-telefone"
                    name="telefone"
                    type="tel"
                    placeholder="9XX XXX XXX"
                    value={form.telefone}
                    onChange={handleChange}
                    required
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="preinscricao-row">
                <div className="preinscricao-field">
                  <label htmlFor="preinscricao-nif">NIF</label>
                  <input
                    id="preinscricao-nif"
                    name="nif"
                    type="text"
                    placeholder="123456789"
                    maxLength={9}
                    value={form.nif}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>
                <div className="preinscricao-field">
                  <label htmlFor="preinscricao-dob">Data de Nascimento *</label>
                  <input
                    id="preinscricao-dob"
                    name="dataNascimento"
                    type="date"
                    value={form.dataNascimento}
                    onChange={handleChange}
                    required
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              {/* Modalidade is selected directly in the header dropdown */}

              {status === 'error' && (
                <div className="preinscricao-error-msg">
                  <AlertCircle size={16} />
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                className="preinscricao-btn"
                disabled={status === 'loading'}
                id="preinscricao-submit"
              >
                {status === 'loading' ? (
                  <>
                    <Loader size={16} className="preinscricao-spinner" />
                    A enviar...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Enviar Pedido
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PreInscricaoModal;
